const trustedExternalOrigins = new Set(['https://www.nhs.uk']);

function isLocalPath(candidate: string): boolean {
  return candidate.startsWith('/');
}

function getSafeFallback(fallback: string): string {
  if (fallback.startsWith('/')) {
    return getTrustedLocalPath(fallback, '/');
  }

  try {
    const candidateUrl = new URL(fallback);
    return isTrustedAbsoluteUrl(candidateUrl) ? candidateUrl.toString() : '/';
  } catch {
    return '/';
  }
}

function isTrustedAbsoluteUrl(candidateUrl: URL): boolean {
  return candidateUrl.protocol === 'https:' && trustedExternalOrigins.has(candidateUrl.origin);
}

function isTrustedLocalUrl(candidateUrl: URL): boolean {
  return candidateUrl.origin === globalThis.location.origin && !candidateUrl.pathname.startsWith('//');
}

export function getTrustedLocalPath(candidate: string | null | undefined, fallback = '/'): string {
  const safeFallback = fallback === '/' ? '/' : getSafeFallback(fallback);
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return safeFallback.startsWith('/') ? safeFallback : '/';
  }

  try {
    const candidateUrl = new URL(candidate, globalThis.location.origin);
    return isTrustedLocalUrl(candidateUrl)
      ? `${candidateUrl.pathname}${candidateUrl.search}${candidateUrl.hash}`
      : safeFallback.startsWith('/') ? safeFallback : '/';
  } catch {
    return safeFallback.startsWith('/') ? safeFallback : '/';
  }
}

export function getTrustedNavigationTarget(candidate: string | null | undefined, fallback = '/'): string {
  const safeFallback = getSafeFallback(fallback);
  if (!candidate) {
    return safeFallback;
  }

  if (candidate.startsWith('/')) {
    return getTrustedLocalPath(candidate, safeFallback);
  }

  try {
    const candidateUrl = new URL(candidate);
    if (isTrustedLocalUrl(candidateUrl)) {
      return `${candidateUrl.pathname}${candidateUrl.search}${candidateUrl.hash}`;
    }

    return isTrustedAbsoluteUrl(candidateUrl) ? candidateUrl.toString() : safeFallback;
  } catch {
    return safeFallback;
  }
}

export function getTrustedReturnPath(locationHref: string, fallback = '/'): string {
  const currentUrl = new URL(locationHref);
  return getTrustedLocalPath(currentUrl.searchParams.get('returnPath'), fallback);
}

export function navigateToTrustedTarget(candidate: string | null | undefined, fallback = '/'): void {
  const target = getTrustedNavigationTarget(candidate, fallback);
  if (isLocalPath(target)) {
    globalThis.history.pushState({}, '', target);
    globalThis.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  globalThis.location.assign(target);
}